import {GetterTree} from "vuex";
import {set} from "vue";
import {IdentifiableObject, NumberOrString} from "../model/identifiable-object";

export interface SetObjectsPayload<T> {
  typeName: string;
  objects: ReadonlyArray<T>;
  keyOf: (object: T) => NumberOrString;
  modifiedAt?: (object: T) => number | Date | undefined;
}

export interface RemoveObjectPayload<T> {
  typeName: string;
  keyOf: (object: T) => NumberOrString;
  predicate: (object: T) => boolean;
}

export const state = () => ({
  typeName2Ids: {} as Record<string, Map<NumberOrString, unknown>>,
  type2Timestamp: {} as Record<string, number>,
});

export type ObjectStoreState = ReturnType<typeof state>;

export const mutations = {
  setObjects<T>(state: ObjectStoreState, data: SetObjectsPayload<T>) {
    let id2Objects = state.typeName2Ids[data.typeName];
    if (id2Objects == null) {
      id2Objects = new Map();
      set(state.typeName2Ids, data.typeName, id2Objects);
    }

    let maxTime = state.type2Timestamp[data.typeName] ?? 0;
    for (const object of data.objects) {
      id2Objects.set(data.keyOf(object), object);
      const modified = data.modifiedAt?.(object);
      const time = modified instanceof Date ? modified.getTime() : modified;
      if (time != null && Number.isFinite(time)) {
        maxTime = Math.max(maxTime, time);
      }
    }

    if (data.modifiedAt != null) {
      set(state.type2Timestamp, data.typeName, maxTime);
    }
  },

  removeBy<T>(state: ObjectStoreState, data: RemoveObjectPayload<T>): void {
    const subMap = state.typeName2Ids[data.typeName];
    if (subMap == null) {
      return;
    }
    for (const [key, obj] of subMap) {
      if (data.predicate(obj as T)) {
        subMap.delete(key);
      }
    }
  },

  clear(state: ObjectStoreState): void {
    state.typeName2Ids = {};
    state.type2Timestamp = {};
  },
};

export const getters: GetterTree<ObjectStoreState, any> = {
  /**
   * @return `null` if no data was put inside the store. Otherwise, will return all objects for this particular type.
   * @param state
   */
  getAllObjectsByType: <T extends IdentifiableObject>(state: ObjectStoreState) => (typeName: string): Array<T> | null => {
    const typeName2Id = state.typeName2Ids[typeName];
    if (typeName2Id == null) {
      return null;
    }
    return Array.from(typeName2Id.values()) as any;
  },

  getAllObjectsByTypeMap: <T extends IdentifiableObject>(state: ObjectStoreState) => (typeName: string): Map<NumberOrString, T> | null => {
    const typeName2Id = state.typeName2Ids[typeName];
    if (typeName2Id == null) {
      return null;
    }
    return typeName2Id as Map<number, T>;
  },

  getObject: (state: ObjectStoreState) => (typeName: string, id: NumberOrString): IdentifiableObject | undefined => {
    const typeName2Id = state.typeName2Ids[typeName];
    if (typeName2Id == null) {
      return undefined;
    }

    const obj = typeName2Id.get(id) as IdentifiableObject | undefined;
    if (obj == null) {
      return undefined;
    }
    return obj;
  },

  getAllKeysForType: (state: ObjectStoreState) => (typename: string): Array<NumberOrString> | null => {
    return Array.from(state.typeName2Ids[typename].keys()) as Array<NumberOrString>;
  },

  getTimestamp: (state: ObjectStoreState) => (typeName: string): number | null => {
    return state.type2Timestamp[typeName];
  },
};

export interface OrderStore {
  getAllObjectsByType<T extends IdentifiableObject>(typeName: string): Array<T> | null;
}

declare module "vuex/types/index" {
  interface Store<S> {
    $orderStore: OrderStore;
  }
}
